import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useEffect } from 'react';

import { Button } from './ui/button';

interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

export function ErrorFallback({
  error,
  componentStack,
  eventId,
  resetError,
}: ErrorFallbackProps) {
  const isDev = import.meta.env.DEV;

  // Convert unknown error to Error object
  const errorObj = error instanceof Error ? error : new Error(String(error));

  useEffect(() => {
    // Log to Sentry if not already captured
    if (error && !eventId) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: componentStack || 'N/A',
          },
        },
      });
    }
  }, [error, eventId, componentStack]);

  const handleReload = () => {
    window.location.reload();
  };

  const handleReset = () => {
    resetError();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-8 shadow-lg">
        <div className="mb-6 flex justify-center">
          <div className="rounded-full bg-red-100 p-3">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <h1 className="mb-2 text-center text-2xl font-bold text-neutral-800">
          Oops! Ceva nu a mers bine
        </h1>

        <p className="mb-6 text-center text-neutral-600">
          Ne cerem scuze pentru inconvenient. O eroare neașteptată a apărut în
          aplicație.
        </p>

        {isDev && (
          <div className="mb-6 rounded-lg bg-neutral-100 p-4">
            <p className="mb-2 text-sm font-semibold text-neutral-700">
              Detalii eroare (doar în development):
            </p>
            <p className="mb-2 font-mono text-xs text-red-600">
              {errorObj.message}
            </p>
            {componentStack && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-neutral-600 hover:text-neutral-800">
                  Component Stack
                </summary>
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-xs text-neutral-600">
                  {componentStack}
                </pre>
              </details>
            )}
            {eventId && (
              <p className="mt-2 text-xs text-neutral-500">
                Event ID: {eventId}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            onClick={handleReset}
            variant="default"
            className="w-full"
            size="lg">
            <RefreshCw className="mr-2 h-4 w-4" />
            Încearcă din nou
          </Button>

          <Button
            onClick={handleReload}
            variant="outline"
            className="w-full"
            size="lg">
            Reîncarcă pagina
          </Button>
        </div>

        <p className="mt-6 text-center text-xs text-neutral-500">
          Dacă problema persistă, te rugăm să ne contactezi.
        </p>
      </div>
    </div>
  );
}
